import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { OracleUsIndexSearchResponseDto, PtpIndexSearchResponseDto } from './search-response.dto';
import { PreInitSettingsService } from 'src/app/core/services/settings/pre-init-setting.service';
import { Observable, map, of } from 'rxjs';
import { ADPResourceService } from 'digital-core';
import { EssItemGroup } from './ess-item-group';

@Injectable({
  providedIn: 'root'
})
export class EquipmentSearchService {

  private readonly apiVersion = '2021-04-30-Preview';
  private readonly ptpEquipmentIndexBaseUrl: string;
  private readonly apiKey;
  private readonly oracleUsEquipmentIndexBaseUrl: string;
  private cachedMakes: any;

  public constructor(
    private httpClient: HttpClient,
    private preInitSettingsService: PreInitSettingsService,
    private resourceService: ADPResourceService,
  ) {
    this.ptpEquipmentIndexBaseUrl = this.preInitSettingsService.getSettings()?.equipmentSearchApi.baseUrl;
    this.apiKey = this.preInitSettingsService.getSettings()?.equipmentSearchApi.key;
    this.oracleUsEquipmentIndexBaseUrl = this.resourceService.getPortalSetting('Global_Ess_OracleUsEquipment_Index_BaseUrl').Value;
  }

  public search(query: string) {

    const filterClause = this.resourceService.getPortaLabelValue('Global_Ess_PtpEquipment_Search_FilterClause');

    // parse incoming string and reconstruct with wildcard search at end of each word part
    let queryStr: string = '';
    const splitString = query.split(" ");
    if(splitString.length > 0){
      splitString.forEach((str: string) => {
        if(str.length>0){
          if(queryStr.length>0){
            queryStr += ' ';
          }
          queryStr += str.replace(/-/g, '\\-') +'*';
        }
      });
    }

    const url = `${this.ptpEquipmentIndexBaseUrl}?api-version=${this.apiVersion}&searchMode=all&${filterClause}&search=${queryStr?.trim() ?? ''}`;

    const headers: HttpHeaders = new HttpHeaders({
      'Ocp-Apim-Subscription-Key': this.apiKey,
    });

    return this.httpClient.get<PtpIndexSearchResponseDto>(url, { headers: headers });
  }

  public manufacturersFromPtpIndex() {
    if (this.cachedMakes) {
      return of(this.cachedMakes);
    }
    const filter = this.resourceService.getPortalSetting('DeviceDetails_NoDevice_Filter_Manufacturers').Value;
    const url = `${this.ptpEquipmentIndexBaseUrl}?api-version=${this.apiVersion}&searchMode=all&facet=Manufacturer,count:3000,sort:value&$count=true&$top=0${filter}`;
    const headers: HttpHeaders = new HttpHeaders({
      'Ocp-Apim-Subscription-Key': this.apiKey,
    });

    return this.httpClient.get<PtpIndexSearchResponseDto>(url, { headers: headers }).pipe(
      map(resp => {
        this.cachedMakes = resp;
        return resp;
      })
    );
  }

  /**
   * This returns the list of all the Manufacturers that produce the specified
   * type of devices that also have a 'WiFi Only' carrier option.
   *
   * The returned manufacturers will be results from the Oracle US Equipment
   * index.
   *
   * Example return result:
   * [ 'Apple', 'Google', 'Samsung', 'Xiaomi' ]
   *
   * @param deviceType The type of device to find the manufacturers of (like
   * phone, tablet, smart watch, etc...). This is required.
   */
  // and MarketingName ne null - this will remove null models
  public getManufacturersOfWiFiOnlyDevicesFromOracleIndex(deviceType: EssItemGroup): Observable<string[]> {
    const filterClause = `$filter=ItemGroup eq '${deviceType}' and Attributes/Carrier eq 'Wi-Fi Only' and MarketingName ne null`;
    return this.getMakes(filterClause);
  }

  public getManufacturers(deviceType: EssItemGroup): Observable<string[]> {
    const filterClause = `$filter=ItemGroup eq '${deviceType}' and MarketingName ne null`;
    return this.getMakes(filterClause);
  }

  private getMakes(filterClause: string): Observable<string[]> {
    const url = `${this.oracleUsEquipmentIndexBaseUrl}?api-version=${this.apiVersion}&searchMode=all&facet=Manufacturer,count:3000,sort:value&$count=false&$top=0&search=*&${filterClause}`;
    const headers: HttpHeaders = new HttpHeaders({
      'Ocp-Apim-Subscription-Key': this.apiKey,
    });

    return this.httpClient.get<any>(url, { headers: headers }).pipe(
      map(resp => {
        const facetObjectArray: { Value: string }[] = resp?.Facets?.Manufacturer;
        return facetObjectArray?.map((item) => item?.Value);
      }),
    );
  }

  /**
   * This returns the list of all the device models (marketing names) of the given device type
   * that have a wi-fi only carrier option and that are also manufactured by
   * the given manufacturer.
   *
   * The returned models will be results from the Oracle US Equipment
   * index.
   *
   * Example return result:
   * [ 'Galaxy Watch 42', 'Galaxy Watch 46', 'Galaxy Watch Active2 40', 'Galaxy Watch Active2 44 Golf', .... ]
   *
   * @param manufacturer The manufacturer of the devices which you want
   * a list of device models for. This is required.
   *
   * @param deviceType The type of device to find the manufacturers of (like
   * phone, tablet, smart watch, etc...). This is required.
   */
  public getModelsOfWiFiOnlyDevicesForManufacturerFromOracleIndex(manufacturer: string, deviceType: EssItemGroup): Observable<string[]> {
    const filterClause = `$filter=ItemGroup eq '${deviceType}' and Attributes/Carrier eq 'Wi-Fi Only' and Manufacturer eq '${manufacturer.replace('&', '%26')}'`;
    return this.getModels(filterClause);
  }

  public getAllModels(manufacturer: string, deviceType: EssItemGroup): Observable<string[]> {
    const filterClause = `$filter=ItemGroup eq '${deviceType}' and Manufacturer eq '${manufacturer.replace('&', '%26')}'`;
    return this.getModels(filterClause);
  }

  private getModels(filterClause: string): Observable<string[]> {
    const url = `${this.oracleUsEquipmentIndexBaseUrl}?api-version=${this.apiVersion}&searchMode=all&facet=MarketingName,count:3000,sort:value&$count=false&$top=0&search=*&${filterClause}`;
    const headers: HttpHeaders = new HttpHeaders({
      'Ocp-Apim-Subscription-Key': this.apiKey,
    });

    return this.httpClient.get<any>(url, { headers: headers }).pipe(
      map(resp => {
        const facetObjectArray: { Value: string }[] = resp?.Facets?.MarketingName;
        return facetObjectArray?.map((item) => item?.Value);
      }),
    );
  }

  // TODO: document the comments similar the the above methods
  public findWiFiOnlyDevicesByManufacturerAndModelAndDeviceTypeFromOracleIndex(manufacturer: string, model: string, deviceType: EssItemGroup): Observable<OracleUsIndexSearchResponseDto> {
    const filterClause = `$filter=ItemGroup eq '${deviceType}' and Attributes/Carrier eq 'Wi-Fi Only' and Manufacturer eq '${manufacturer.replace('&', '%26')}' and MarketingName eq '${model}'`;
    const url = `${this.oracleUsEquipmentIndexBaseUrl}?api-version=${this.apiVersion}&searchMode=all&$top=1000&${filterClause}`;
    const headers: HttpHeaders = new HttpHeaders({
      'Ocp-Apim-Subscription-Key': this.apiKey,
    });

    return this.httpClient.get<OracleUsIndexSearchResponseDto>(url, { headers: headers });
  }
}
